import React, { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import IconPicker from './IconPicker';
import { iconLabel } from '../utils/iconLabel';
import PlayableBoard from './PlayableBoard';
import { localChecker } from '../utils/gameChecker';
import { CatalogIcon } from '../utils/iconSearch';
import { gameStatus, sortGames, scheduleWarnings } from '../utils/schedule';
import './AdminPage.css';
import { API_BASE } from '../config';
import './Board.css'; // Import Board.css for fonts

const GROUP_COLORS = ['#9B5DE5', '#3A86FF', '#00BB7E', '#FFBE0B'];
// Drag and drop between the icon library and the slots
const DRAG_TYPE = 'application/x-matchset-icon';
type DragData = { from: 'library'; file: string } | { from: 'slot'; g: number; i: number };

const RECENT_DAYS = 28; // "recent" = used in a game within the last 4 weeks

const emptyGroups = () => Array.from({ length: 4 }, () => Array(4).fill(''));


const AdminPage: React.FC = () => {
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    error: authError
  } = useAuth0();

  const [catalog, setCatalog] = useState<CatalogIcon[] | null>(null);
  const [catalogError, setCatalogError] = useState('');
  const [groups, setGroups] = useState<string[][]>(emptyGroups());
  const [groupNames, setGroupNames] = useState<string[]>(["", "", "", ""]);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState('');
  const [games, setGames] = useState<any[]>([]);
  const [editingGame, setEditingGame] = useState<any | null>(null);
  const [published, setPublished] = useState(true);
  const [stats, setStats] = useState<Record<string, any>>({});
  const [testing, setTesting] = useState<{ icons: string[]; key: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ g: number; i: number } | null>(null);

  // Logout function
  const handleLogout = () => {
    logout({ 
      logoutParams: { 
        returnTo: `${window.location.origin}/admin` 
      } 
    });
  };

  // Fetch games after Auth0 authentication
  useEffect(() => {
    if (isAuthenticated) {
      // Load the icon catalog (static file, used for search)
      fetch('/icon-catalog.json')
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data: CatalogIcon[]) => setCatalog(data))
        .catch((err) => {
          console.error('Error loading icon catalog:', err);
          setCatalogError('Could not load the icon library. Refresh the page to try again.');
        });

      // Fetch games with Auth0 token
      const fetchGames = async () => {
        try {
          const token = await getAccessTokenSilently();
          const response = await axios.get(`${API_BASE}/api/games`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (Array.isArray(response.data)) {
            setGames(response.data);
            loadStats(token);
          } else {
            console.error('Games API did not return an array:', typeof response.data);
          }
        } catch (error: any) {
          console.error('Error fetching games:', error);
          const status = error?.response?.status;
          if (status === 403) {
            setMessage('Error: your account is not authorized to manage games.');
          } else if (status === 401) {
            setMessage('Error: your session could not be verified. Try logging out and back in.');
          } else if (status === 503) {
            setMessage('Error: admin login is not configured on the server.');
          }
        }
      };
      
      fetchGames();
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Play stats per game (anonymous); failures just leave stats blank
  const loadStats = (token: string) => {
    axios
      .get(`${API_BASE}/api/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStats(r.data || {}))
      .catch((err) => console.error('Error loading stats:', err));
  };

  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const scrollToForm = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const resetForm = () => {
    setEditingGame(null);
    setGroups(emptyGroups());
    setGroupNames(["", "", "", ""]);
    setName('');
    setStartDate('');
    setEndDate('');
    setPublished(true);
  };

  // Edit game
  const handleEdit = (game: any) => {
    setEditingGame(game);
    setName(game.name);
    setStartDate(game.startDate?.slice(0, 10) || '');
    setEndDate(game.endDate?.slice(0, 10) || '');
    setGroups(game.groups.map((g: any[]) => g.map((t: any) => t.icon)));
    setGroupNames(game.groupNames || ["", "", "", ""]);
    setPublished(game.published !== false);
    setMessage('');
    scrollToForm();
  };

  // Start a new game from a copy of an existing one (new dates, saved as a draft)
  const handleDuplicate = (game: any) => {
    setEditingGame(null);
    setName(`Copy of ${game.name}`);
    setStartDate('');
    setEndDate('');
    setGroups(game.groups.map((g: any[]) => g.map((t: any) => t.icon)));
    setGroupNames(game.groupNames || ["", "", "", ""]);
    setPublished(false);
    setMessage(`Copied "${game.name}". Set new dates, then save.`);
    scrollToForm();
  };

  const sortedGames = useMemo(() => sortGames(games), [games]);
  const warnings = useMemo(() => scheduleWarnings(games), [games]);

  // Delete game
  const handleDelete = async (gameId: string) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${API_BASE}/api/games/${gameId}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setMessage('Game deleted!');
      refreshGames();
    } catch (error) {
      setMessage('Error deleting game');
      console.error('Delete error:', error);
    }
  };

  // Fetch games after create/edit/delete
  const refreshGames = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${API_BASE}/api/games`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(response.data)) {
        setGames(response.data);
        loadStats(token);
      }
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const handleGroupChange = (groupIdx: number, tileIdx: number, value: string) => {
    setGroups(prev => {
      const copy = prev.map(row => [...row]);
      copy[groupIdx][tileIdx] = value;
      return copy;
    });
  };

  // Icons already chosen for this game
  const inUse = useMemo(() => new Set(groups.flat().filter(Boolean)), [groups]);

  // Icons and group names used in any other game. Uses from the last 12 weeks
  // (or scheduled ahead) are marked "recent". The latest use wins.
  const { usedBefore, namesUsedBefore } = useMemo(() => {
    const cutoff = Date.now() - RECENT_DAYS * 86400000;
    const icons = new Map<string, { label: string; recent: boolean }>();
    const names = new Map<string, string>();
    [...games]
      .filter((g) => !(editingGame && g._id === editingGame._id))
      .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)))
      .forEach((g) => {
        const label = `"${g.name}" (${String(g.startDate).slice(0, 10)})`;
        const recent = !!g.endDate && new Date(g.endDate).getTime() >= cutoff;
        (g.groups || []).flat().forEach((t: any) => t?.icon && icons.set(t.icon, { label, recent }));
        (g.groupNames || []).forEach((n: string) => n && n.trim() && names.set(n.trim().toLowerCase(), label));
      });
    return { usedBefore: icons, namesUsedBefore: names };
  }, [games, editingGame]);

  const startDrag = (e: React.DragEvent, data: DragData) => {
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(data));
    e.dataTransfer.effectAllowed = 'copyMove';
    // Custom pill-shaped drag picture: the browser's default snapshot shows
    // white corners around the rounded tile
    const file = data.from === 'library' ? data.file : groups[data.g][data.i];
    const color = data.from === 'slot' ? GROUP_COLORS[data.g] : '#1C1C1E';
    const ghost = document.createElement('div');
    ghost.style.cssText = `position:fixed;top:-200px;left:-200px;width:104px;height:56px;border-radius:200px;background:${color};display:flex;align-items:center;justify-content:center;pointer-events:none;`;
    const img = document.createElement('img');
    img.src = `/icons/${file}`;
    img.style.cssText = 'width:30px;height:30px;filter:brightness(0) invert(1);';
    ghost.appendChild(img);
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 52, 28);
    setTimeout(() => ghost.remove(), 0);
  };

  const readDrag = (e: React.DragEvent): DragData | null => {
    try {
      return JSON.parse(e.dataTransfer.getData(DRAG_TYPE));
    } catch {
      return null;
    }
  };

  // Drop onto a slot: from the library it fills/replaces; from another slot it swaps
  const handleDropOnSlot = (e: React.DragEvent, g: number, i: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    const data = readDrag(e);
    if (!data) return;
    const flat = groups.flat();
    const target = g * 4 + i;
    if (data.from === 'library') {
      if (flat.includes(data.file) && flat[target] !== data.file) return; // already in this game
      flat[target] = data.file;
    } else {
      const source = data.g * 4 + data.i;
      if (source === target) return;
      [flat[source], flat[target]] = [flat[target], flat[source]];
    }
    setGroups([0, 1, 2, 3].map((r) => flat.slice(r * 4, r * 4 + 4)));
  };

  // Click an icon: add it to the next open spot (Group 1 left to right, then Group 2...)
  const handleAddIcon = (file: string) => {
    const flat = groups.flat();
    if (flat.includes(file)) return;
    const next = flat.findIndex((t) => !t);
    if (next === -1) {
      setMessage('All 16 spots are full. Remove an icon (×) or drag one onto a spot to replace it.');
      return;
    }
    handleGroupChange(Math.floor(next / 4), next % 4, file);
  };

  // Drop a placed icon back on the library to remove it
  const handleDropOnLibrary = (e: React.DragEvent) => {
    const data = readDrag(e);
    if (!data || data.from !== 'slot') return;
    e.preventDefault();
    handleGroupChange(data.g, data.i, '');
  };

  // Submit create/edit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !startDate || !endDate || groups.some(g => g.some(t => !t))) {
      setMessage('Please fill all fields and select all icons.');
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      
      if (editingGame) {
        await axios.put(`${API_BASE}/api/games/${editingGame._id}`, {
          name,
          startDate,
          endDate,
          groups: groups.map(g => g.map(icon => ({ icon }))) ,
          groupNames,
          published
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage(published ? 'Game updated!' : 'Draft saved. Players won\'t see it until you publish it.');
      } else {
        await axios.post(`${API_BASE}/api/games`, {
          name,
          startDate,
          endDate,
          groups: groups.map(g => g.map(icon => ({ icon }))) ,
          groupNames,
          published
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage(published ? 'Game created!' : 'Draft saved. Players won\'t see it until you publish it.');
      }
      resetForm();
      refreshGames();
    } catch (err) {
      let errorMsg = 'Unknown error';
      let errObj: any = err;
      if (
        typeof err === 'object' &&
        err &&
        'response' in err &&
        errObj.response &&
        errObj.response.data &&
        errObj.response.data.error
      ) {
        errorMsg = errObj.response.data.error;
      } else if (err instanceof Error) {
        errorMsg = err.message;
      }
      setMessage('Error creating/updating game: ' + errorMsg);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-container">
        <div className="admin-login-form">
          <h1>Matchset Admin</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="admin-container">
        <div className="admin-login-form">
          <h1>Matchset Admin</h1>
          <div className="admin-message error" style={{ marginBottom: '20px' }}>
            Auth error: {authError.message}
          </div>
          <button
            onClick={() => loginWithRedirect()}
            className="admin-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="admin-login-form">
          <h1>Matchset Admin</h1>
          <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
            Secure authentication powered by Auth0
          </p>
          <button
            onClick={() => loginWithRedirect()}
            className="admin-button"
          >
            Login with Auth0
          </button>
          {message && <div className="admin-message error">{message}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-main">
        <div className="admin-header">
          {user && (
            <div className="admin-welcome">
              <div>Welcome, {user.name || user.email}</div>
              <div style={{ fontSize: '12px' }}>Secured by Auth0</div>
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <h1>Matchset</h1>
            <h2>Admin Dashboard</h2>
          </div>
          <button 
            className="admin-logout-button"
            onClick={handleLogout}
            title="Logout"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16,17 21,12 16,7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
        
        {warnings.length > 0 && (
          <div className="admin-schedule-warnings" role="status">
            <strong>Schedule check</strong>
            <ul>
              {warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          </div>
        )}

        <div className="admin-form-card">
          <h3 className="admin-form-title">{editingGame ? 'Edit Game' : 'Create New Game'}</h3>
          <form onSubmit={handleSubmit}>
            <input 
              type="text" 
              placeholder="Game name" 
              value={name} 
              onChange={e => setName(e.target.value)}
              className="admin-input"
              style={{ marginBottom: '20px' }}
            />
            
            <div className="admin-input-group">
              <label>
                Start Date
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)}
                  className="admin-input-date"
                />
              </label>
              <label>
                End Date
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={e => setEndDate(e.target.value)}
                  className="admin-input-date"
                />
              </label>
            </div>

            <div className="admin-publish-toggle">
              <button
                type="button"
                role="switch"
                aria-checked={published}
                className={`admin-switch${published ? ' on' : ''}`}
                onClick={() => setPublished((v) => !v)}
              >
                <span className="admin-switch-knob" />
              </button>
              <span>
                <strong>{published ? 'Published' : 'Unpublished (draft)'}</strong>
                <span className="admin-publish-help">
                  {published
                    ? 'Players will see this puzzle on its dates.'
                    : 'Hidden from players until you switch it on.'}
                </span>
              </span>
            </div>
            
            <div className="admin-editor">
              {/* Left: the four rows, like the game board */}
              <div className="admin-editor-rows">
                <h4 className="admin-groups-title">Groups</h4>
                {groups.map((group, groupIdx) => (
                  <div key={groupIdx} className="admin-row-block">
                    <div className="admin-group-header">
                      <span className="admin-group-label">Group {groupIdx + 1}</span>
                      <input
                        type="text"
                        placeholder="Group name"
                        value={groupNames[groupIdx] || ""}
                        onChange={e => setGroupNames(names => names.map((n, i) => i === groupIdx ? e.target.value : n))}
                        className="admin-group-name-input"
                      />
                    </div>
                    {(() => {
                      const prior = namesUsedBefore.get((groupNames[groupIdx] || '').trim().toLowerCase());
                      return prior ? (
                        <div className="admin-name-used">This group name was used before in {prior}</div>
                      ) : null;
                    })()}
                    <div className="admin-row" style={{ borderColor: GROUP_COLORS[groupIdx] }}>
                      {group.map((tile, tileIdx) => {
                        const over = dropTarget?.g === groupIdx && dropTarget?.i === tileIdx;
                        return (
                          <div
                            key={tileIdx}
                            className={`admin-slot${tile ? ' filled' : ''}${over ? ' drop-over' : ''}`}
                            style={tile ? { background: GROUP_COLORS[groupIdx] } : undefined}
                            title={tile ? `${iconLabel(tile)} (drag to move)` : 'Drag an icon here'}
                            draggable={!!tile}
                            onDragStart={(e) => tile && startDrag(e, { from: 'slot', g: groupIdx, i: tileIdx })}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              if (!over) setDropTarget({ g: groupIdx, i: tileIdx });
                            }}
                            onDragLeave={() => setDropTarget(null)}
                            onDrop={(e) => handleDropOnSlot(e, groupIdx, tileIdx)}
                            onDragEnd={() => setDropTarget(null)}
                            aria-label={`Group ${groupIdx + 1}, icon ${tileIdx + 1}${tile ? `: ${iconLabel(tile)}` : ': empty'}`}
                          >
                            {tile ? <img src={`/icons/${tile}`} alt="" draggable={false} /> : '+'}
                            {tile && (
                              <button
                                type="button"
                                className="admin-slot-clear"
                                aria-label="Remove icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGroupChange(groupIdx, tileIdx, '');
                                }}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Right: the icon library. Drop a placed icon here to remove it. */}
              <div
                className="admin-editor-library"
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes(DRAG_TYPE)) e.preventDefault();
                }}
                onDrop={handleDropOnLibrary}
              >
                <h4 className="admin-groups-title">Icon library</h4>
                <p className="admin-picker-hint">
                  Click an icon to add it to the next open spot, or drag it into a specific spot. Drag placed icons to move them; use × or drag one back here to remove it.
                </p>
                <IconPicker
                  catalog={catalog}
                  catalogError={catalogError}
                  inUse={inUse}
                  usedBefore={usedBefore}
                  onPick={handleAddIcon}
                  onDragIcon={(e, file) => startDrag(e, { from: 'library', file })}
                />
              </div>
            </div>

            <div className="admin-button-group">
              <button
                type="button"
                className="admin-button secondary"
                disabled={groups.some((g) => g.some((t) => !t))}
                title={groups.some((g) => g.some((t) => !t)) ? 'Fill all 16 icons to test the puzzle' : 'Play this puzzle exactly as players will'}
                onClick={() => {
                  const icons = groups.flat();
                  for (let i = icons.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [icons[i], icons[j]] = [icons[j], icons[i]];
                  }
                  setTesting({ icons, key: Date.now() });
                }}
              >
                Test puzzle
              </button>
              <button type="submit" className="admin-button">
                {editingGame ? 'Update Game' : 'Create Game'}
              </button>
              {editingGame && (
                <button 
                  type="button" 
                  className="admin-button secondary"
                  onClick={() => { resetForm(); setMessage(''); }}
                >
                  Cancel Edit
                </button>
              )}
            </div>
            
            {message && (
              <div className={`admin-message ${message.includes('Error') || message.includes('failed') ? 'error' : 'success'}`}>
                {message}
              </div>
            )}
          </form>
        </div>
        
        <div className="admin-games-list">
          <h3>Schedule</h3>
          {games.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>No games created yet</p>
          ) : (
            <ul>
              {sortedGames.map(game => (
                <li key={game._id} className="admin-game-item">
                  <div className="admin-game-info">
                    <span className={`admin-status admin-status-${gameStatus(game).toLowerCase()}`}>{gameStatus(game)}</span>
                    <strong>{game.name}</strong>
                    <span className="admin-game-dates">
                      ({game.startDate?.slice(0,10)} to {game.endDate?.slice(0,10)})
                    </span>
                    {(() => {
                      const st = stats[game._id];
                      const status = gameStatus(game);
                      if (!st) {
                        return status === 'Live' || status === 'Past'
                          ? <div className="admin-game-stats muted">No plays recorded yet</div>
                          : null;
                      }
                      const pct = st.finished ? Math.round((st.solved / st.finished) * 100) : 0;
                      return (
                        <div className="admin-game-stats">
                          <span><b>{st.started}</b> started</span>
                          <span><b>{st.finished}</b> finished</span>
                          <span><b>{st.solved}</b> solved{st.finished ? ` (${pct}%)` : ''}</span>
                          {st.avgTries != null && <span>avg <b>{st.avgTries}</b> tries to solve</span>}
                          {st.avgSeconds != null && <span>avg time <b>{fmtTime(st.avgSeconds)}</b></span>}
                          {st.solved > 0 && (
                            <span className="admin-game-stats-dist" title="How many players solved it in 1, 2, 3 or 4 tries">
                              Solved in 1/2/3/4 tries: {st.solvedIn.join(' · ')}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="admin-game-actions">
                    <button className="admin-button secondary" onClick={() => handleEdit(game)}>
                      Edit
                    </button>
                    <button className="admin-button secondary" onClick={() => handleDuplicate(game)}>
                      Duplicate
                    </button>
                    <button className="admin-button danger" onClick={() => handleDelete(game._id)}>
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {testing && (
        <div className="admin-test-overlay" role="dialog" aria-label="Test puzzle">
          <div className="admin-test-bar">
            <span><b>Test run</b> · Nothing is saved, shared or counted in stats.</span>
            <button type="button" className="admin-button secondary" onClick={() => setTesting(null)}>
              Close test
            </button>
          </div>
          <div className="admin-test-body">
            <PlayableBoard
              key={testing.key}
              icons={testing.icons}
              checker={localChecker(groups, groupNames)}
              startDate={startDate ? `${startDate}T00:00:00.000Z` : undefined}
              isPreview
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;